import { useMemo } from 'react';

import { KeyValueRow, KeyValueTable } from '@/design-system/components/ui';

import { EventTriggerType, Trigger } from '../domain';
import { getTriggerEventType } from '../domain/mappers';
import {
  AmbientTradeTriggerFullDetails,
  AquariusSwapsTriggerFullDetails,
  AquariusTriggerFullDetails,
  BlendTriggerFullDetails,
  CompoundV3TriggerFullDetails,
  CustomEventTriggerFullDetails,
  DuneQueryTriggerFullDetails,
  EulerV2LoopingTriggerFullDetails,
  EulerVaultsTriggerFullDetails,
  ExponentTriggerFullDetails,
  FelixCdpCollateralTriggerFullDetails,
  FelixVanillaCollateralTriggerFullDetails,
  FogoIgnitionLockedTriggerFullDetails,
  FogoLendTriggerFullDetails,
  FogoTokenHoldersTriggerFullDetails,
  GalxeQuestTriggerFullDetails,
  GearboxTriggerFullDetails,
  HibachiAffiliatesTriggerFullDetails,
  HibachiTradingTriggerFullDetails,
  HyperliquidTradeTriggerFullDetails,
  HypurrCardTransactionsTriggerFullDetails,
  HypurrEulerNetPositionsTriggerFullDetails,
  HypurrNetPoolPositionsTriggerFullDetails,
  LynxTriggerFullDetails,
  MorphoTriggerFullDetails,
  MorphoVaultsTriggerFullDetails,
  OdysseyBuilderTradesTriggerFullDetails,
  OnChainTriggerFullDetails,
  OrderlyTriggerFullDetails,
  PacificaBuilderTradesTriggerFullDetails,
  PendleLPTriggerFullDetails,
  PendleYTTriggerFullDetails,
  PolymarketTradeVolumeTriggerFullDetails,
  PyronTriggerFullDetails,
  SolanaTokenHoldersTriggerFullDetails,
  StellarTokenHoldersTriggerFullDetails,
  StripePaymentTriggerFullDetails,
  SubgraphTriggerFullDetails,
  SuiTokenHoldersTriggerFullDetails,
  TokenHolderTriggerFullDetails,
  UniswapLiquidityPoolTriggerFullDetails,
  UniswapLiquidityV4TriggerFullDetails,
  ValiantLiquidityTriggerFullDetails,
  ValiantTradeTriggerFullDetails,
  XFollowTriggerFullDetails,
  XLikeTriggerFullDetails,
  XPostTriggerFullDetails,
  XRepostTriggerFullDetails,
  ZealyQuestTriggerFullDetails,
} from './trigger-full-details';
import { MorphoBorrowerStrategyTriggerFullDetails } from './trigger-full-details/MorphoBorrowerStrategyTriggerFullDetails';

interface Props {
  trigger: Trigger;
}

export default function TriggerFullDetails({ trigger }: Props) {
  const triggerType = useMemo(() => getTriggerEventType(trigger), [trigger]);

  if (!trigger) {
    return null;
  }

  // Return specific components based on trigger type
  if (
    triggerType === EventTriggerType.OnChain ||
    triggerType === EventTriggerType.OnChainFunction ||
    triggerType === EventTriggerType.NadoTrade ||
    triggerType === EventTriggerType.NadoPurpsTrade
  ) {
    return <OnChainTriggerFullDetails trigger={trigger} />;
  }

  if (
    triggerType === EventTriggerType.CustomEvent ||
    triggerType === EventTriggerType.Github ||
    triggerType === EventTriggerType.Discord
  ) {
    return <CustomEventTriggerFullDetails trigger={trigger} />;
  }

  if (
    triggerType === EventTriggerType.OnChainTokenHolder ||
    triggerType === EventTriggerType.BalancerPool ||
    triggerType === EventTriggerType.OnChainV2Liquidity
  ) {
    return <TokenHolderTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.Galxe) {
    return <GalxeQuestTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.Zealy) {
    return <ZealyQuestTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.Dune) {
    return <DuneQueryTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.OnChainSubgraphEvent) {
    return <SubgraphTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.PendleLP) {
    return <PendleLPTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.Pendle) {
    return <PendleYTTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.LynxTrade) {
    return <LynxTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.XFollowEvent) {
    return <XFollowTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.XPostEvent) {
    return <XPostTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.XRepostEvent) {
    return <XRepostTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.XLikeEvent) {
    return <XLikeTriggerFullDetails trigger={trigger} />;
  }

  if (
    triggerType === EventTriggerType.Uniswap ||
    triggerType === EventTriggerType.HybraLiquidityV3 ||
    triggerType === EventTriggerType.HyperswapLiquidityV3 ||
    triggerType === EventTriggerType.PrjXLiquidityV3 ||
    triggerType === EventTriggerType.KittenSwapLiquidityV3 ||
    triggerType === EventTriggerType.UltrasolidLiquidityV3 ||
    triggerType === EventTriggerType.UpheavalLiquidityV3 ||
    triggerType === EventTriggerType.NestLiquidityV3
  ) {
    return <UniswapLiquidityPoolTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.UniswapV4) {
    return <UniswapLiquidityV4TriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.Morpho) {
    return <MorphoTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.MorphoVaults) {
    return <MorphoVaultsTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.MorphoBorrowerStrategy) {
    return <MorphoBorrowerStrategyTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.CompoundV3) {
    return <CompoundV3TriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.HypurrNetPoolPositions) {
    return <HypurrNetPoolPositionsTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.HypurrEulerNetPositions) {
    return <HypurrEulerNetPositionsTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.HibachiTrading) {
    return <HibachiTradingTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.HibachiAffiliates) {
    return <HibachiAffiliatesTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.AmbientTrade) {
    return <AmbientTradeTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.HyperliquidTrade) {
    return <HyperliquidTradeTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.ValiantTrade) {
    return <ValiantTradeTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.ValiantLiquidity) {
    return <ValiantLiquidityTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.OrderlyBuilderTrades) {
    return <OrderlyTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.SolanaTokenHolders) {
    return <SolanaTokenHoldersTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.SuiTokenHolders) {
    return <SuiTokenHoldersTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.FogoTokenHolders) {
    return <FogoTokenHoldersTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.StellarTokenHolders) {
    return <StellarTokenHoldersTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.AquariusLiquidity) {
    return <AquariusTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.AquariusSwaps) {
    return <AquariusSwapsTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.BlendPositions) {
    return <BlendTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.Pyron) {
    return <PyronTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.Exponent) {
    return <ExponentTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.FogoLend) {
    return <FogoLendTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.FogoIgnitionLocked) {
    return <FogoIgnitionLockedTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.FelixVanillaCollateral) {
    return <FelixVanillaCollateralTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.FelixCdpCollateral) {
    return <FelixCdpCollateralTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.HypurrCardTransactions) {
    return <HypurrCardTransactionsTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.PolymarketTradeVolume) {
    return <PolymarketTradeVolumeTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.Gearbox) {
    return <GearboxTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.PacificaBuilderTrades) {
    return <PacificaBuilderTradesTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.OdysseyBuilderTrades) {
    return <OdysseyBuilderTradesTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.EulerV2Looping) {
    return <EulerV2LoopingTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.EulerVaults) {
    return <EulerVaultsTriggerFullDetails trigger={trigger} />;
  }

  if (triggerType === EventTriggerType.StripePayment) {
    return <StripePaymentTriggerFullDetails trigger={trigger} />;
  }

  // Default table for other trigger types
  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow label="Argument conditions" value={trigger.conditionExpression ?? '-'} />
    </KeyValueTable>
  );
}
